import { Controller } from "@hotwired/stimulus"

// Vendor brochure studio — live preview. Renders the 10-theme picker and
// the selected theme's 10-color menu, and applies the choice to the
// in-page brochure (.vbr-doc) instantly by swapping its data-theme and
// --brand CSS variable. The hidden theme/color fields ride along so the
// "Download brochure PDF" form asks the server for the same combination.
export default class extends Controller {
  static targets = ["themes", "colors", "themeField", "colorField", "preview", "logoScaleField"]
  static values = {
    themes: Array,   // [{ key, name, primary, colors:[10 hex] }]
    theme:  String,
    color:  String
  }

  connect() {
    this.doc = this.element.querySelector(".vbr-doc")
    this._renderThemes()
    this._renderColors()
    this._apply()
  }

  // ---- picker rendering ----
  _renderThemes() {
    this.themesTarget.innerHTML = this.themesValue.map(t => `
      <button type="button" class="vbs-swatch vbs-theme${t.key === this.themeValue ? " is-active" : ""}"
              data-action="vendor-brochure#selectTheme" data-theme-key="${t.key}"
              title="${this._esc(t.name)}">
        <span class="vbs-swatch-dot" style="background:${t.primary}"></span>
        <span class="vbs-swatch-label">${this._esc(t.name)}</span>
      </button>`).join("")
  }

  _renderColors() {
    const theme = this._theme()
    const colors = (theme && theme.colors) || []
    this.colorsTarget.innerHTML = colors.map(c => `
      <button type="button" class="vbs-chip${c.toLowerCase() === this.colorValue.toLowerCase() ? " is-active" : ""}"
              data-action="vendor-brochure#selectColor" data-color="${c}"
              style="background:${c}" title="${c}"></button>`).join("")
  }

  // ---- actions ----
  selectTheme(e) {
    const key = e.currentTarget.dataset.themeKey
    if (key === this.themeValue) return
    this.themeValue = key
    // Reset to the new theme's default color unless the current color
    // already belongs to it.
    const theme = this._theme()
    const colors = (theme && theme.colors) || []
    if (!colors.some(c => c.toLowerCase() === this.colorValue.toLowerCase())) {
      this.colorValue = colors[4] || colors[0] || this.colorValue
    }
    this._renderThemes()
    this._renderColors()
    this._apply()
  }

  selectColor(e) {
    this.colorValue = e.currentTarget.dataset.color
    this._renderColors()
    this._apply()
  }

  // Logo size slider → --logo-scale on the preview + the download field.
  scaleLogo(e) {
    const v = e.target.value
    if (this.doc) this.doc.style.setProperty("--logo-scale", v)
    if (this.hasLogoScaleFieldTarget) this.logoScaleFieldTarget.value = v
  }

  // ---- apply to preview + form ----
  _apply() {
    if (this.doc) {
      this.doc.dataset.theme = this.themeValue
      this.doc.style.setProperty("--brand", this.colorValue)
    }
    if (this.hasThemeFieldTarget) this.themeFieldTarget.value = this.themeValue
    if (this.hasColorFieldTarget) this.colorFieldTarget.value = this.colorValue
  }

  _theme() {
    return this.themesValue.find(t => t.key === this.themeValue) || this.themesValue[0]
  }

  _esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
